import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Flame, Dumbbell, Mail, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import useLenis from "@/hooks/useLenis";
import Header from "@/components/Header";
import { Logo } from "@/components/Logo";

const PROGRAM_TITLES = {
  ascent: "Ascent",
  altitude: "Altitude",
  stillness: "Stillness",
  "iron-order": "Iron Order",
};

const Dashboard = () => {
  useLenis();
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [checkoutStatus, setCheckoutStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/dashboard/summary");
        if (alive) setSummary(res.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Poll for checkout session status when returning from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      if (stop || attempts >= 6) {
        setCheckoutStatus((s) => s || { payment_status: "timeout" });
        return;
      }
      try {
        const r = await api.get(`/checkout/status/${sid}`);
        setCheckoutStatus(r.data);
        if (r.data.payment_status === "paid" || r.data.status === "expired") {
          if (r.data.payment_status === "paid") {
            toast.success("Payment received. Your reservation is sealed.");
          }
          return;
        }
      } catch (_e) {
        /* keep polling */
      }
      attempts += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [params]);

  const handleLogout = () => {
    logout();
    // Hard navigation avoids the ProtectedRoute race that pins logout to /login.
    window.location.assign("/");
  };

  return (
    <div className="relative bg-black text-white min-h-screen">
      <Header />
      <main className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/10 pb-10 mb-12">
            <div>
              <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                §DASHBOARD · MEMBER
              </div>
              <h1 className="font-serif text-white text-[12vw] md:text-[6vw] leading-[0.9] tracking-[-0.02em]">
                Welcome,{" "}
                <span className="italic text-silver-100">
                  {user?.name?.split(" ")[0] || "warrior"}.
                </span>
              </h1>
              <div className="mt-3 flex items-center gap-3 font-mono text-[10px] tracking-[0.32em] text-white/50">
                <span>{user?.email}</span>
                <span>·</span>
                <span>{user?.role?.toUpperCase()}</span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors self-start md:self-auto"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              <span className="text-[11px] tracking-[0.3em] uppercase">Sign Out</span>
            </button>
          </div>

          {/* Checkout return banner */}
          {checkoutStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-10 border rounded-md p-6 flex items-center justify-between gap-4 ${
                checkoutStatus.payment_status === "paid"
                  ? "border-silver-100/50 bg-white/[0.03]"
                  : "border-white/15 bg-white/[0.02]"
              }`}
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-1">
                  §CHECKOUT STATUS
                </div>
                <div className="font-serif italic text-2xl md:text-3xl text-silver-100">
                  {checkoutStatus.payment_status === "paid"
                    ? "Payment received. Reservation sealed."
                    : checkoutStatus.payment_status === "timeout"
                    ? "We couldn't confirm the payment yet — check your email."
                    : "Payment is being processed…"}
                </div>
              </div>
              <button
                onClick={() => {
                  setCheckoutStatus(null);
                  const p = new URLSearchParams(params);
                  p.delete("session_id");
                  setParams(p, { replace: true });
                }}
                className="text-white/50 hover:text-white text-xs tracking-[0.3em] uppercase"
              >
                Dismiss
              </button>
            </motion.div>
          )}

          {loading ? (
            <div className="py-40 text-center font-mono text-[10px] tracking-[0.42em] text-silver-300">
              <span className="inline-block w-2 h-2 rounded-full bg-silver-100 animate-pulse mr-3" />
              LOADING SIGNATURE…
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-8 md:gap-10">
              {/* Left column: Turn 180 + Newsletter */}
              <div className="col-span-12 md:col-span-4 space-y-6">
                {/* Turn 180 card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  data-testid="dashboard-challenge-card"
                  className="relative overflow-hidden border border-silver-100/30 rounded-md p-6 bg-gradient-to-b from-white/[0.05] to-black"
                >
                  <div className="absolute -right-8 -top-8 opacity-[0.08]">
                    <Logo showWordmark={false} className="h-40 w-40" />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <Flame className="h-5 w-5 text-silver-100" strokeWidth={1.4} />
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                      §TURN 180
                    </div>
                  </div>
                  {summary?.challenge ? (
                    <>
                      <div className="font-serif italic text-silver-100 text-3xl md:text-4xl leading-[1.05]">
                        Day 1 sealed.
                      </div>
                      <div className="mt-3 text-white/60 text-[13px] leading-[1.7]">
                        Committed as{" "}
                        <span className="text-white">
                          {summary.challenge.name}
                        </span>{" "}
                        · Number
                        <span className="text-silver-100 font-mono tracking-[0.24em] ml-1">
                          {summary.challenge.numbered_id}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-serif text-white text-3xl md:text-4xl leading-[1.02]">
                        180 days.
                        <br />
                        <span className="italic text-silver-100">One decision.</span>
                      </div>
                      <Link
                        to="/challenge#commit"
                        className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-silver-100 text-black hover:bg-white transition-colors"
                      >
                        <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
                          Sign the Covenant
                        </span>
                      </Link>
                    </>
                  )}
                </motion.div>

                {/* Newsletter card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  className="border border-white/10 rounded-md p-6 bg-white/[0.02]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Mail className="h-5 w-5 text-silver-100" strokeWidth={1.4} />
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                      §DISPATCH
                    </div>
                  </div>
                  {summary?.newsletter ? (
                    <>
                      <div className="font-serif text-white text-xl">Enlisted.</div>
                      <div className="mt-2 font-mono text-[11px] tracking-[0.32em] text-silver-100">
                        {summary.newsletter.numbered_id}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-white/60 text-[13px] leading-[1.7]">
                        One quiet letter, one Sunday a month.
                      </div>
                      <Link
                        to="/#enlist"
                        className="mt-4 inline-flex items-center gap-2 text-silver-100 hover:text-white text-[11px] tracking-[0.28em] uppercase"
                      >
                        Enlist →
                      </Link>
                    </>
                  )}
                </motion.div>

                {/* Profile card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.15 }}
                  className="border border-white/10 rounded-md p-6 bg-white/[0.02]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <User className="h-5 w-5 text-silver-100" strokeWidth={1.4} />
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                      §PROFILE
                    </div>
                  </div>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-white/50">Name</span>
                      <span className="text-white">{user?.name}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-white/50">Email</span>
                      <span className="text-white break-all text-right">{user?.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Since</span>
                      <span className="text-white">
                        {user?.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right column: Enrollments */}
              <div className="col-span-12 md:col-span-8">
                <div className="flex items-end justify-between border-b border-white/10 pb-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Dumbbell className="h-5 w-5 text-silver-100" strokeWidth={1.4} />
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                      §PROGRAMS · ENROLLED
                    </div>
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.32em] text-white/40">
                    {summary?.enrollments?.length || 0} ACTIVE
                  </div>
                </div>

                {summary?.enrollments?.length ? (
                  <div className="space-y-4" data-testid="dashboard-enrollments">
                    {summary.enrollments.map((e, i) => (
                      <motion.div
                        key={e.id || i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="grid grid-cols-12 gap-4 items-center border border-white/10 rounded-md p-6 bg-white/[0.02] hover:border-white/25 transition-colors"
                      >
                        <div className="col-span-12 md:col-span-2 font-serif italic text-silver-200 text-4xl md:text-5xl">
                          N°{" "}
                          {e.numbered_id?.replace(/^N°\s*/, "").slice(0, 3) || "01"}
                        </div>
                        <div className="col-span-12 md:col-span-7">
                          <div className="font-mono text-[10px] tracking-[0.32em] text-silver-300 mb-1">
                            {(e.program_slug || "").toUpperCase()}
                          </div>
                          <div className="font-serif text-white text-2xl md:text-3xl">
                            {PROGRAM_TITLES[e.program_slug] || e.program_slug}
                          </div>
                          {e.goal && (
                            <div className="mt-2 text-white/60 text-[13px] italic">
                              &ldquo;{e.goal}&rdquo;
                            </div>
                          )}
                        </div>
                        <div className="col-span-12 md:col-span-3 text-left md:text-right">
                          <div className="font-mono text-[10px] tracking-[0.32em] text-white/40">
                            ENROLLED
                          </div>
                          <div className="mt-1 text-white/70 text-[13px]">
                            {e.enrolled_at
                              ? new Date(e.enrolled_at).toLocaleDateString()
                              : "—"}
                          </div>
                          <Link
                            to={`/programs/${e.program_slug}`}
                            className="mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-silver-100 hover:text-white"
                          >
                            Open →
                          </Link>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div
                    data-testid="dashboard-empty-enrollments"
                    className="border border-dashed border-white/15 rounded-md p-10 text-center"
                  >
                    <div className="font-serif italic text-silver-100 text-3xl md:text-4xl leading-[1.05]">
                      No programs yet.
                    </div>
                    <p className="mt-3 text-white/55 text-[13px] leading-[1.7] max-w-md mx-auto">
                      Choose your first ascent. Ascent · Altitude · Stillness ·
                      Iron Order.
                    </p>
                    <Link
                      to="/#programs"
                      className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-silver-100 text-black hover:bg-white transition-colors"
                    >
                      <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
                        Browse Programs
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
